import { motion } from "motion/react";

import { JSX, useState } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/utils/cn";

interface AvatarProps {
  type: "round-1" | "round-2" | "square";
  link: string;
  imageSrc: string;
  name: string;
  role?: string;
  className?: string;
}

const AvatarCard = ({ type, link, imageSrc, name, role, className }: AvatarProps): JSX.Element => {
  const [isLoaded, setLoad] = useState(false);

  return (
    <Link
      to={link}
      aria-label={name}
      className={cn(
        {
          "group flex flex-col items-center justify-start gap-[0.938rem]": type === "round-1",
          "flex flex-col gap-[0.625rem] text-center": type === "square",
        },
        className,
      )}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={isLoaded || !imageSrc ? { opacity: 1 } : {}}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="size-24 overflow-hidden rounded-xl "
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={name}
            onLoad={() => setLoad(true)}
            loading={"lazy"}
            className={cn({
              "responsive-transition aspect-square w-full rounded-full object-cover object-center outline outline-[0.188rem] -outline-offset-[0.188rem] outline-transparent group-hover:outline-orange-1 group-active:outline-orange-1":
                type === "round-1",
              "aspect-square w-full rounded-[1.25rem] object-cover object-center": type === "square",
            })}
          />
        ) : (
          <div className="aspect-square h-full w-full rounded-full">
            <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="50" fill="#C7C6C6" />
              <mask id="mask0_1529_15606" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
                <circle cx="50" cy="50" r="50" fill="#C4C4C4" />
              </mask>
              <g mask="url(#mask0_1529_15606)">
                <ellipse cx="50" cy="88" rx="38" ry="22" fill="#303031" />
              </g>
              <ellipse cx="50" cy="42.5" rx="18" ry="18" fill="#303031" />
            </svg>
          </div>
        )}
        {!isLoaded && imageSrc && (
          <div className="aspect-square size-[7.5rem] w-full animate-pulse  rounded-full bg-surface-bright" />
        )}
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <p
          className={cn({
            "responsive-transition line-clamp-2 text-center text-sm font-medium text-on-primary group-hover:text-on-primary":
              type === "round-1",
            "!text-white": role,
          })}
        >
          {name}
        </p>
        {role ? <p className={"mt-2 text-center text-on-surface"}>{role}</p> : null}
      </motion.div>
    </Link>
  );
};

export default AvatarCard;
